import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import AddIcon from "@mui/icons-material/Add";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavigateFunction } from "react-router-dom";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../SharedComponents/Pagination";
import { SearchInput } from "../SharedComponents/SearchInput";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { calculateTotalPages } from "../utils/pagination";
import { IUserResponse } from "../clients/answeringClient";
import { CouponCard } from "./CouponCard";
import { CouponCodeCard } from "./CouponCodeCard";
import {
  CreateCouponDialog,
  type CreateCouponFormState,
} from "./CreateCouponDialog";
import {
  CreateCouponCodeDialog,
  type CreateCouponCodeFormState,
} from "./CreateCouponCodeDialog";
import {
  CouponsController,
  CouponsControllerDelegate,
  CouponListItem,
  CouponCodeListItem,
  CouponDetail,
  CreateCouponCodePayload,
  CreateCouponPayload,
  DiscountType,
  ListCouponsParams,
  PaginatedCoupons,
  UpdateCouponPayload,
  UpdateCouponCodePayload,
} from "./controller";

type ActiveFilter = "all" | "active" | "inactive";

interface FiltersState {
  internalName: string;
  active: ActiveFilter;
  page: number;
  total: number;
  limit: number;
}

interface Props {
  navigationHooks: NavigateFunction;
}

const buildInitialFormState = (): CreateCouponFormState => ({
  internalName: "",
  discountType: "percent",
  percentOff: "",
  amountOff: "",
  minOrderAmount: "",
  startsAt: "",
  endsAt: "",
  active: true,
});

const detailToFormState = (coupon: CouponDetail): CreateCouponFormState => {
  const base = buildInitialFormState();
  const formState: CreateCouponFormState = {
    ...base,
    internalName: coupon.internalName,
    discountType: coupon.discountType,
    active: coupon.active,
    startsAt: coupon.startsAt ?? "",
    endsAt: coupon.endsAt ?? "",
    minOrderAmount: formatCentsToInput(coupon.minOrderAmountCents),
  };

  if (coupon.discountType === "percent") {
    formState.percentOff = formatPercentToInput(coupon.percentOff);
    formState.amountOff = "";
  } else {
    formState.amountOff = formatCentsToInput(coupon.amountOffCents);
    formState.percentOff = "";
  }

  return formState;
};

const initialFilters: FiltersState = {
  internalName: "",
  active: "all",
  page: 1,
  total: 0,
  limit: 9,
};

const COUPON_CODES_PAGE_SIZE = 10;

interface CouponCodesState {
  isLoading: boolean;
  data: CouponCodeListItem[];
  page: number;
  limit: number;
  total: number;
  error?: string;
}

const parseNumber = (value: string): number | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseDateInput = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const formatCentsToInput = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return (value / 100).toFixed(2).replace(".", ",");
};

const formatPercentToInput = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const getDiscountTypeLabel = (discountType: DiscountType): string =>
  discountType === "percent" ? "Percentual de " : "Valor fixo de ";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formatCurrencyFromCents = (
  value: number | null | undefined,
  emptyLabel = "-"
): string => {
  if (value === null || value === undefined) {
    return emptyLabel;
  }
  return currencyFormatter.format(value / 100);
};

const formatDate = (value: string): string => {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }
  return parsed.toLocaleString("pt-BR");
};

const formatOptionalDate = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }
  return formatDate(value);
};

const formatPercentLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "-";
  }
  return `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const getCouponDiscountValueLabel = (coupon: CouponListItem): string =>
  coupon.discountType === "percent"
    ? formatPercentLabel(coupon.percentOff)
    : formatCurrencyFromCents(coupon.amountOffCents);

const getCouponMinOrderLabel = (
  minOrderAmountCents: number | null | undefined
): string => formatCurrencyFromCents(minOrderAmountCents, "Sem pedido mínimo");

const buildValidityLabel = (
  startsAt?: string | null,
  endsAt?: string | null
): string => {
  const hasStart = Boolean(startsAt);
  const hasEnd = Boolean(endsAt);

  if (!hasStart && !hasEnd) {
    return "Sem período definido";
  }

  const startLabel = formatOptionalDate(startsAt);
  const endLabel = formatOptionalDate(endsAt);

  if (hasStart && hasEnd) {
    return `${startLabel} até ${endLabel}`;
  }

  if (hasStart) {
    return `A partir de ${startLabel}`;
  }

  return `Até ${endLabel}`;
};

const getCouponValidityLabel = (coupon: CouponListItem): string => {
  return buildValidityLabel(coupon.startsAt, coupon.endsAt);
};

const getCouponCodeValidityLabel = (code: CouponCodeListItem): string => {
  return buildValidityLabel(code.startsAt, code.endsAt);
};

const mapAssignedUsersToIUserResponses = (
  code: CouponCodeListItem
): IUserResponse[] => {
  const normalizedUsers =
    code.assignedUsers
      ?.map((user) => ({
        uid: user.uid ?? "",
        email: user.email ?? "",
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
      }))
      .filter((user) => Boolean(user.uid)) ?? [];

  const existingUids = new Set(normalizedUsers.map((user) => user.uid));
  const fallbackUsers =
    code.assignedUserUid
      ?.filter((uid): uid is string => Boolean(uid))
      .filter((uid) => !existingUids.has(uid))
      .map((uid) => ({
        uid,
        email: "",
        firstName: "",
        lastName: "",
      })) ?? [];

  return [...normalizedUsers, ...fallbackUsers];
};

const buildInitialCouponCodeFormState = (): CreateCouponCodeFormState => ({
  code: "",
  assignedUserUids: [],
  assignedInstrumentIds: [],
  usageLimit: "",
  usageType: "global",
  startsAt: "",
  endsAt: "",
  active: true,
});

const Coupons: React.FC<Props> = ({ navigationHooks }) => {
  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [isLoading, setIsLoading] = useState(true);
  const [coupons, setCoupons] = useState<CouponListItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formValues, setFormValues] = useState<CreateCouponFormState>(
    () => buildInitialFormState()
  );
  const [formError, setFormError] = useState<string>();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [pendingReload, setPendingReload] = useState(false);
  const [expandedCoupons, setExpandedCoupons] = useState<Record<string, boolean>>(
    {}
  );
  const [couponCodesState, setCouponCodesState] = useState<
    Record<string, CouponCodesState>
  >({});
  const couponCodesStateRef = useRef(couponCodesState);
  useEffect(() => {
    couponCodesStateRef.current = couponCodesState;
  }, [couponCodesState]);
  const [isCodeDialogOpen, setIsCodeDialogOpen] = useState(false);
  const [codeFormMode, setCodeFormMode] = useState<"create" | "edit">("create");
  const [codeFormValues, setCodeFormValues] =
    useState<CreateCouponCodeFormState>(() => buildInitialCouponCodeFormState());
  const [codeFormInitialUsers, setCodeFormInitialUsers] = useState<
    IUserResponse[]
  >([]);
  const [codeFormError, setCodeFormError] = useState<string>();
  const [isCodeSubmitting, setIsCodeSubmitting] = useState(false);
  const [selectedCouponForCode, setSelectedCouponForCode] = useState<{
    id: string;
    name: string;
    startsAt: string | null;
    endsAt: string | null;
  } | null>(null);
  const [editingCouponCodeId, setEditingCouponCodeId] = useState<string | null>(null);
  const [codeDeleteTarget, setCodeDeleteTarget] = useState<{
    couponId: string;
    couponName: string;
    codeId: string;
    codeValue: string;
  } | null>(null);
  const [deleteCodeError, setDeleteCodeError] = useState<string>();
  const [isDeletingCode, setIsDeletingCode] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string>();
  const [deletingCoupon, setDeletingCoupon] = useState(false);

  const controllerRef = useRef<CouponsController>();

  const delegate: CouponsControllerDelegate = useMemo(
    () => ({
      didFetchCoupons(payload: PaginatedCoupons) {
        setFilters((prev) => ({
          ...prev,
          page: payload.page,
          limit: payload.limit,
          total: payload.total,
        }));
        setCoupons(payload.data);
        setIsLoading(false);
        setErrorMessage(undefined);
      },
      didFailToFetchCoupons(message?: string) {
        setIsLoading(false);
        setErrorMessage(message ?? "Erro ao carregar cupons.");
      },
      didFetchCoupon(coupon: CouponDetail) {
        setIsFormLoading(false);
        setFormError(undefined);
        setFormValues(detailToFormState(coupon));
      },
      didFailToFetchCoupon(message?: string) {
        setIsFormLoading(false);
        setFormError(message ?? "Erro ao carregar o cupom.");
      },
      didCreateCoupon(_coupon: CouponListItem) {
        setIsFormSubmitting(false);
        setFormError(undefined);
        setIsFormDialogOpen(false);
        setFormValues(buildInitialFormState());
        setFormMode("create");
        setEditingCouponId(null);
        setFilters((prev) => ({
          ...prev,
          page: 1,
        }));
        setPendingReload(true);
      },
      didFailToCreateCoupon(message?: string) {
        setIsFormSubmitting(false);
        setFormError(message ?? "Erro ao criar o cupom.");
      },
      didUpdateCoupon(updatedCoupon: CouponListItem) {
        setIsFormSubmitting(false);
        setFormError(undefined);
        setIsFormDialogOpen(false);
        setFormMode("create");
        setEditingCouponId(null);
        setFormValues(buildInitialFormState());
        setCoupons((prev) =>
          prev.map((coupon) =>
            coupon.id === updatedCoupon.id ? updatedCoupon : coupon
          )
        );
      },
      didFailToUpdateCoupon(message?: string) {
        setIsFormSubmitting(false);
        setFormError(message ?? "Erro ao atualizar o cupom.");
      },
      didDeleteCoupon(couponId: string) {
        setDeletingCoupon(false);
        setDeleteError(undefined);
        setDeleteTarget(null);
        if (editingCouponId === couponId) {
          setIsFormDialogOpen(false);
          setEditingCouponId(null);
          setFormMode("create");
          setFormValues(buildInitialFormState());
          setIsFormSubmitting(false);
          setIsFormLoading(false);
        }
        setCoupons((prev) => prev.filter((coupon) => coupon.id !== couponId));
        setFilters((prev) => {
          const nextTotal = Math.max(prev.total - 1, 0);
          const maxPage = Math.max(
            1,
            Math.ceil(nextTotal / prev.limit)
          );
          const nextPage = Math.min(prev.page, maxPage);
          return {
            ...prev,
            total: nextTotal,
            page: nextPage,
          };
        });
        setExpandedCoupons((prev) => {
          if (!(couponId in prev)) {
            return prev;
          }
          const { [couponId]: _removed, ...rest } = prev;
          return rest;
        });
        setCouponCodesState((prev) => {
          if (!(couponId in prev)) {
            return prev;
          }
          const { [couponId]: _omit, ...rest } = prev;
          return rest;
        });
        setPendingReload(true);
      },
      didFailToDeleteCoupon(message?: string) {
        setDeletingCoupon(false);
        setDeleteError(message ?? "Erro ao excluir o cupom.");
      },
      shouldLogout() {
        logout();
        navigationHooks("/auth");
      },
    }),
    [navigationHooks, editingCouponId]
  );

  if (!controllerRef.current) {
    controllerRef.current = new CouponsController(delegate);
  }

  useEffect(() => {
    controllerRef.current = new CouponsController(delegate);
  }, [delegate]);

  const loadCoupons = useCallback(() => {
    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    const currentFilters = filtersRef.current;
    const params: ListCouponsParams = {
      page: currentFilters.page,
      limit: currentFilters.limit,
    };

    const internalName = currentFilters.internalName.trim();
    if (internalName) {
      params.internalName = internalName;
    }

    if (currentFilters.active === "active") {
      params.active = true;
    } else if (currentFilters.active === "inactive") {
      params.active = false;
    }

    setIsLoading(true);
    setErrorMessage(undefined);

    controllerRef.current?.listCoupons(accessToken, params);
  }, [navigationHooks]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  useEffect(() => {
    if (!pendingReload) {
      return;
    }
    setPendingReload(false);
    loadCoupons();
  }, [pendingReload, loadCoupons]);

  const fetchCouponCodes = useCallback(
    (couponId: string, page = 1, append = false) => {
      const accessToken = localStorage.getItem("AccessToken");
      if (!accessToken) {
        logout();
        navigationHooks("/auth");
        return;
      }

      const currentState = couponCodesStateRef.current[couponId];
      const limit = currentState?.limit ?? COUPON_CODES_PAGE_SIZE;

      setCouponCodesState((prev) => {
        const previous = prev[couponId];
        const data = previous?.data ?? [];
        return {
          ...prev,
          [couponId]: {
            data,
            page: previous?.page ?? 0,
            limit,
            total: previous?.total ?? 0,
            isLoading: true,
            error: undefined,
          },
        };
      });

      controllerRef.current
        ?.listCouponCodes(accessToken, { couponId, page, limit })
        .then((payload) => {
          setCouponCodesState((prev) => {
            const previous = prev[couponId];
            const mergedData =
              append && previous
                ? [...previous.data, ...payload.data]
                : payload.data;
            return {
              ...prev,
              [couponId]: {
                data: mergedData,
                page: payload.page,
                limit: payload.limit,
                total: payload.total,
                isLoading: false,
                error: undefined,
              },
            };
          });
        })
        .catch((error: any) => {
          const message =
            error?.message ?? "Erro ao carregar os códigos do cupom.";
          setCouponCodesState((prev) => {
            const previous = prev[couponId];
            return {
              ...prev,
              [couponId]: {
                data: previous?.data ?? [],
                page: previous?.page ?? 0,
                limit: previous?.limit ?? COUPON_CODES_PAGE_SIZE,
                total: previous?.total ?? 0,
                isLoading: false,
                error: message,
              },
            };
          });
        });
    },
    [navigationHooks]
  );

  const handleToggleCoupon = useCallback(
    (couponId: string, isExpanded: boolean) => {
      setExpandedCoupons((prev) => ({
        ...prev,
        [couponId]: isExpanded,
      }));
      if (isExpanded) {
        const current = couponCodesStateRef.current[couponId];
        if (!current || current.data.length === 0) {
          fetchCouponCodes(couponId);
        }
      }
    },
    [fetchCouponCodes]
  );

  const handleLoadMoreCodes = useCallback(
    (couponId: string) => {
      const state = couponCodesStateRef.current[couponId];
      if (!state || state.isLoading) {
        return;
      }
      if (state.data.length >= state.total) {
        return;
      }
      const nextPage = state.page + 1;
      fetchCouponCodes(couponId, nextPage, true);
    },
    [fetchCouponCodes]
  );

  const handleOpenCreateCodeDialog = useCallback((coupon: CouponListItem) => {
    setSelectedCouponForCode({
      id: coupon.id,
      name: coupon.internalName,
      startsAt: coupon.startsAt,
      endsAt: coupon.endsAt,
    });
    setCodeFormValues(buildInitialCouponCodeFormState());
    setCodeFormInitialUsers([]);
    setCodeFormError(undefined);
    setCodeFormMode("create");
    setEditingCouponCodeId(null);
    setIsCodeDialogOpen(true);
  }, []);

  const handleOpenEditCodeDialog = useCallback(
    (coupon: CouponListItem, code: CouponCodeListItem) => {
      setSelectedCouponForCode({
        id: coupon.id,
        name: coupon.internalName,
        startsAt: coupon.startsAt,
        endsAt: coupon.endsAt,
      });
      const normalizedAssignedInstrumentIds = Array.isArray(
        code.assignedInstrumentId
      )
        ? code.assignedInstrumentId
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        : [];
      const assignedInstrumentIdsFromObjects =
        code.assignedInstruments
          ?.map((instrument) => Number(instrument?.id))
          .filter((id) => Number.isFinite(id) && id > 0) ?? [];
      const resolvedAssignedInstrumentIds =
        normalizedAssignedInstrumentIds.length > 0
          ? normalizedAssignedInstrumentIds
          : assignedInstrumentIdsFromObjects;
      const usageType = code.usageType ?? "global";
      const usageLimitValue =
        usageType === "global" && code.usageLimit !== null
          ? String(code.usageLimit)
          : "";
      setCodeFormValues({
        code: code.code.toLowerCase(),
        assignedUserUids: Array.isArray(code.assignedUserUid)
          ? [...code.assignedUserUid]
          : [],
        assignedInstrumentIds: resolvedAssignedInstrumentIds,
        usageLimit: usageLimitValue,
        usageType,
        startsAt: code.startsAt ?? "",
        endsAt: code.endsAt ?? "",
        active: code.active,
      });
      setCodeFormInitialUsers(mapAssignedUsersToIUserResponses(code));
      setCodeFormError(undefined);
      setCodeFormMode("edit");
      setEditingCouponCodeId(code.id);
      setIsCodeDialogOpen(true);
    },
    []
  );

  const handleCloseCodeDialog = useCallback(() => {
    if (isCodeSubmitting) {
      return;
    }
    setIsCodeDialogOpen(false);
    setCodeFormError(undefined);
    setCodeFormValues(buildInitialCouponCodeFormState());
    setCodeFormInitialUsers([]);
    setSelectedCouponForCode(null);
    setEditingCouponCodeId(null);
    setCodeFormMode("create");
  }, [isCodeSubmitting]);

  const handleCodeFormChange = useCallback(
    <K extends keyof CreateCouponCodeFormState>(
      field: K,
      value: CreateCouponCodeFormState[K]
    ) => {
      setCodeFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));
      setCodeFormError(undefined);
    },
    []
  );

  const handleSubmitCodeDialog = useCallback(() => {
    if (!selectedCouponForCode) {
      setCodeFormError("Cupom não encontrado.");
      return;
    }

    const codeValue = codeFormValues.code.trim();
    if (!codeValue) {
      setCodeFormError("Informe o código do cupom.");
      return;
    }

    const assignedUserUids = Array.from(
      new Set(
        (codeFormValues.assignedUserUids ?? [])
          .map((uid) => uid.trim())
          .filter((uid) => uid.length > 0)
      )
    );
    const assignedInstrumentIds = Array.from(
      new Set(
        (codeFormValues.assignedInstrumentIds ?? [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );
    const usageType = codeFormValues.usageType ?? "global";
    const usageLimitValue =
      usageType === "global" ? codeFormValues.usageLimit.trim() : "";

    let usageLimit: number | undefined;
    if (usageType === "global" && usageLimitValue) {
      const parsedUsageLimit = Number(usageLimitValue);
      if (!Number.isInteger(parsedUsageLimit) || parsedUsageLimit <= 0) {
        setCodeFormError(
          "O limite de uso deve ser um número inteiro maior que zero."
        );
        return;
      }
      usageLimit = parsedUsageLimit;
    }

    const startsAt = parseDateInput(codeFormValues.startsAt);
    if (codeFormValues.startsAt && startsAt === null) {
      setCodeFormError("Informe uma data de início válida.");
      return;
    }

    const endsAt = parseDateInput(codeFormValues.endsAt);
    if (codeFormValues.endsAt && endsAt === null) {
      setCodeFormError("Informe uma data de fim válida.");
      return;
    }

    const couponStartsAtTime = selectedCouponForCode.startsAt
      ? new Date(selectedCouponForCode.startsAt).getTime()
      : null;
    const couponEndsAtTime = selectedCouponForCode.endsAt
      ? new Date(selectedCouponForCode.endsAt).getTime()
      : null;
    if (startsAt && couponStartsAtTime !== null) {
      if (new Date(startsAt).getTime() < couponStartsAtTime) {
        setCodeFormError(
          "A vigência do código não pode começar antes da vigência do cupom."
        );
        return;
      }
    }
    if (startsAt && couponEndsAtTime !== null) {
      if (new Date(startsAt).getTime() > couponEndsAtTime) {
        setCodeFormError(
          "A vigência do código não pode começar após o fim da vigência do cupom."
        );
        return;
      }
    }
    if (endsAt && couponStartsAtTime !== null) {
      if (new Date(endsAt).getTime() < couponStartsAtTime) {
        setCodeFormError(
          "A vigência do código não pode terminar antes do início da vigência do cupom."
        );
        return;
      }
    }
    if (endsAt && couponEndsAtTime !== null) {
      if (new Date(endsAt).getTime() > couponEndsAtTime) {
        setCodeFormError(
          "A vigência do código não pode terminar após o fim da vigência do cupom."
        );
        return;
      }
    }

    if (startsAt && endsAt) {
      if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
        setCodeFormError("O fim da vigência deve ser maior que o início.");
        return;
      }
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    setIsCodeSubmitting(true);
    setCodeFormError(undefined);

    const couponId = selectedCouponForCode.id;
    const normalizedCode = codeValue.toLowerCase();

    if (codeFormMode === "create") {
      const payload: CreateCouponCodePayload = {
        couponId,
        code: normalizedCode,
        active: codeFormValues.active,
        usageType,
      };

      if (assignedUserUids.length > 0) {
        payload.assignedUserUid = assignedUserUids;
      }
      if (assignedInstrumentIds.length > 0) {
        payload.assignedInstrumentId = assignedInstrumentIds;
      }
      if (usageType === "global") {
        if (usageLimit !== undefined) {
          payload.usageLimit = usageLimit;
        }
      } else {
        payload.usageLimit = null;
      }
      if (startsAt !== null) {
        payload.startsAt = startsAt;
      }
      if (endsAt !== null) {
        payload.endsAt = endsAt;
      }

      controllerRef.current
        ?.createCouponCode(accessToken, payload)
        .then((createdCode) => {
          setIsCodeSubmitting(false);
          setIsCodeDialogOpen(false);
          setCodeFormValues(buildInitialCouponCodeFormState());
          setCodeFormInitialUsers([]);
          setCodeFormError(undefined);
          setSelectedCouponForCode(null);
          setEditingCouponCodeId(null);
          setCodeFormMode("create");
          setCouponCodesState((prev) => {
            const previous = prev[couponId];
            const data = previous
              ? [createdCode, ...previous.data.filter((item) => item.id !== createdCode.id)]
              : [createdCode];
            return {
              ...prev,
              [couponId]: {
                data,
                total: (previous?.total ?? 0) + 1,
                page: previous?.page ?? 1,
                limit: previous?.limit ?? COUPON_CODES_PAGE_SIZE,
                isLoading: false,
                error: undefined,
              },
            };
          });
        })
        .catch((error: any) => {
          setIsCodeSubmitting(false);
          const message =
            error?.message ?? "Erro ao criar o código de cupom.";
          setCodeFormError(message);
        });
      return;
    }

    if (!editingCouponCodeId) {
      setIsCodeSubmitting(false);
      setCodeFormError("Código não encontrado.");
      return;
    }

    const updatePayload: UpdateCouponCodePayload = {
      code: normalizedCode,
      active: codeFormValues.active,
      usageType,
      assignedUserUid:
        assignedUserUids.length > 0 ? assignedUserUids : null,
      assignedInstrumentId:
        assignedInstrumentIds.length > 0 ? assignedInstrumentIds : null,
      usageLimit:
        usageType === "global"
          ? usageLimitValue === ""
            ? null
            : usageLimit ?? null
          : null,
      startsAt,
      endsAt,
    };

    controllerRef.current
      ?.updateCouponCode(accessToken, editingCouponCodeId, updatePayload)
      .then((updatedCode) => {
        setIsCodeSubmitting(false);
        setIsCodeDialogOpen(false);
        setCodeFormValues(buildInitialCouponCodeFormState());
        setCodeFormInitialUsers([]);
        setCodeFormError(undefined);
        setSelectedCouponForCode(null);
        setEditingCouponCodeId(null);
        setCodeFormMode("create");
        setCouponCodesState((prev) => {
          const previous = prev[updatedCode.couponId];
          if (!previous) {
            return {
              ...prev,
              [updatedCode.couponId]: {
                data: [updatedCode],
                total: 1,
                page: 1,
                limit: COUPON_CODES_PAGE_SIZE,
                isLoading: false,
                error: undefined,
              },
            };
          }
          return {
            ...prev,
            [updatedCode.couponId]: {
              ...previous,
              data: previous.data.map((item) =>
                item.id === updatedCode.id ? updatedCode : item
              ),
              isLoading: false,
              error: undefined,
            },
          };
        });
      })
      .catch((error: any) => {
        setIsCodeSubmitting(false);
        const message =
          error?.message ?? "Erro ao atualizar o código de cupom.";
        setCodeFormError(message);
      });
  }, [
    codeFormMode,
    codeFormValues,
    navigationHooks,
    selectedCouponForCode,
    editingCouponCodeId,
  ]);

  const handleOpenDeleteCodeDialog = useCallback(
    (coupon: CouponListItem, code: CouponCodeListItem) => {
      setCodeDeleteTarget({
        couponId: coupon.id,
        couponName: coupon.internalName,
        codeId: code.id,
        codeValue: code.code.toLowerCase(),
      });
      setDeleteCodeError(undefined);
    },
    []
  );

  const handleCloseDeleteCodeDialog = useCallback(() => {
    if (isDeletingCode) {
      return;
    }
    setCodeDeleteTarget(null);
    setDeleteCodeError(undefined);
  }, [isDeletingCode]);

  const handleConfirmDeleteCode = useCallback(() => {
    if (!codeDeleteTarget) {
      return;
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    setIsDeletingCode(true);
    setDeleteCodeError(undefined);

    const { couponId, codeId } = codeDeleteTarget;

    controllerRef.current
      ?.deleteCouponCode(accessToken, codeId)
      .then(() => {
        setIsDeletingCode(false);
        setCodeDeleteTarget(null);
        setCouponCodesState((prev) => {
          const previous = prev[couponId];
          if (!previous) {
            return prev;
          }
          const nextData = previous.data.filter((item) => item.id !== codeId);
          return {
            ...prev,
            [couponId]: {
              ...previous,
              data: nextData,
              total: Math.max(0, previous.total - 1),
              isLoading: false,
              error: undefined,
            },
          };
        });
      })
      .catch((error: any) => {
        setIsDeletingCode(false);
        const message =
          error?.message ?? "Erro ao excluir o código de cupom.";
        setDeleteCodeError(message);
      });
  }, [codeDeleteTarget, navigationHooks]);

  const handleSearch = useCallback((filter: string) => {
    setFilters((prev) => ({
      ...prev,
      internalName: filter,
      page: 1,
    }));
    setPendingReload(true);
  }, []);

  const handleActiveFilterChange = useCallback(
    (event: SelectChangeEvent<ActiveFilter>) => {
      const value = event.target.value as ActiveFilter;
      if (value === filtersRef.current.active) {
        return;
      }
      setFilters((prev) => ({
        ...prev,
        active: value,
        page: 1,
      }));
      setPendingReload(true);
    },
    []
  );

  const handleOpenCreateDialog = useCallback(() => {
    setFormMode("create");
    setIsFormDialogOpen(true);
    setFormValues(buildInitialFormState());
    setFormError(undefined);
    setEditingCouponId(null);
    setIsFormSubmitting(false);
    setIsFormLoading(false);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    if (isFormSubmitting) {
      return;
    }
    setIsFormDialogOpen(false);
    setFormError(undefined);
    setFormValues(buildInitialFormState());
    setFormMode("create");
    setEditingCouponId(null);
    setIsFormLoading(false);
  }, [isFormSubmitting]);

  const handleFormChange = useCallback(
    <K extends keyof CreateCouponFormState>(
      field: K,
      value: CreateCouponFormState[K]
    ) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));
      setFormError(undefined);
    },
    []
  );

  const handleDiscountTypeChange = useCallback(
    (event: SelectChangeEvent<DiscountType>) => {
      const nextType = event.target.value as DiscountType;
      setFormValues((prev) => {
        if (prev.discountType === nextType) {
          return prev;
        }
        return {
          ...prev,
          discountType: nextType,
          percentOff: nextType === "percent" ? prev.percentOff : "",
          amountOff: nextType === "fixed_amount" ? prev.amountOff : "",
        };
      });
      setFormError(undefined);
    },
    []
  );

  const handleSubmitForm = useCallback(() => {
    const name = formValues.internalName.trim();

    if (!name) {
      setFormError("Informe o nome interno do cupom.");
      return;
    }

    let percentValue: number | null = null;
    let amountValue: number | null = null;

    if (formValues.discountType === "percent") {
      percentValue = parseNumber(formValues.percentOff);
      if (percentValue === null) {
        setFormError("Informe o percentual de desconto.");
        return;
      }
      if (percentValue <= 0 || percentValue > 100) {
        setFormError("O percentual deve estar entre 0 e 100.");
        return;
      }
    } else {
      amountValue = parseNumber(formValues.amountOff);
      if (amountValue === null) {
        setFormError("Informe o valor de desconto em reais.");
        return;
      }
      if (amountValue <= 0) {
        setFormError("O valor de desconto deve ser maior que zero.");
        return;
      }
    }

    const minOrderValue = parseNumber(formValues.minOrderAmount);
    const startsAt = parseDateInput(formValues.startsAt);
    const endsAt = parseDateInput(formValues.endsAt);

    if (formValues.startsAt && startsAt === null) {
      setFormError("Informe uma data de início válida.");
      return;
    }

    if (formValues.endsAt && endsAt === null) {
      setFormError("Informe uma data de fim válida.");
      return;
    }

    if (startsAt && endsAt) {
      if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
        setFormError("O fim da vigência deve ser maior que o início.");
        return;
      }
    }

    const basePayload: UpdateCouponPayload = {
      internalName: name,
      discountType: formValues.discountType,
      active: formValues.active,
      startsAt,
      endsAt,
    };

    if (formValues.discountType === "percent" && percentValue !== null) {
      basePayload.percentOff = Number(percentValue.toFixed(2));
      basePayload.amountOffCents = null;
    } else if (amountValue !== null) {
      basePayload.amountOffCents = Math.round(amountValue * 100);
      basePayload.percentOff = null;
    }

    if (minOrderValue !== null) {
      basePayload.minOrderAmountCents = Math.round(minOrderValue * 100);
    } else {
      basePayload.minOrderAmountCents = null;
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    if (formMode === "edit" && !editingCouponId) {
      setFormError("Cupom não encontrado.");
      return;
    }

    setIsFormSubmitting(true);
    setFormError(undefined);

    if (formMode === "create") {
      const createPayload: CreateCouponPayload = {
        internalName: basePayload.internalName!,
        discountType: basePayload.discountType!,
        active: basePayload.active,
        percentOff: basePayload.percentOff,
        amountOffCents: basePayload.amountOffCents,
        minOrderAmountCents: basePayload.minOrderAmountCents ?? undefined,
        startsAt: basePayload.startsAt ?? undefined,
        endsAt: basePayload.endsAt ?? undefined,
      };
      controllerRef.current?.createCoupon(accessToken, createPayload);
    } else if (editingCouponId) {
      controllerRef.current?.updateCoupon(
        accessToken,
        editingCouponId,
        basePayload
      );
    }
  }, [editingCouponId, formMode, formValues, navigationHooks]);

  const handleOpenEditDialog = useCallback((coupon: CouponListItem) => {
    setEditingCouponId(coupon.id);
    setFormMode("edit");
    setIsFormDialogOpen(true);
    setIsFormSubmitting(false);
    setFormError(undefined);
    setIsFormLoading(true);
    setFormValues((prev) => ({
      ...buildInitialFormState(),
      internalName: coupon.internalName,
      discountType: coupon.discountType,
      active: coupon.active,
    }));

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    controllerRef.current?.getCoupon(accessToken, coupon.id);
  }, [navigationHooks]);

  const handleOpenDeleteDialog = useCallback((coupon: CouponListItem) => {
    setDeleteTarget({ id: coupon.id, name: coupon.internalName });
    setDeleteError(undefined);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    if (deletingCoupon) {
      return;
    }
    setDeleteTarget(null);
    setDeleteError(undefined);
  }, [deletingCoupon]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) {
      setDeleteError("Cupom não encontrado.");
      return;
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      logout();
      navigationHooks("/auth");
      return;
    }

    setDeletingCoupon(true);
    setDeleteError(undefined);
    controllerRef.current?.deleteCoupon(accessToken, deleteTarget.id);
  }, [deleteTarget, navigationHooks]);

  const totalPages = useMemo(
    () => calculateTotalPages(filters.total, filters.limit),
    [filters.limit, filters.total]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const nextPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
      if (nextPage === filters.page) {
        return;
      }
      setFilters((prev) => ({
        ...prev,
        page: nextPage,
      }));
      setPendingReload(true);
    },
    [filters.page, setFilters, setPendingReload, totalPages]
  );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
      }}
    >
      <Layout>
        <Box
          flex={1}
          overflow="auto"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Container maxWidth="lg" sx={{ padding: "80px 20px" }}>
            <Box sx={{ mb: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={3}
                width="100%"
                flexWrap="wrap"
                gap={2}
              >
                <Stack direction="column" spacing={1.25}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Typography variant="h2">Cupons</Typography>
                    <Chip
                      label={
                        filters.total === 1
                          ? "1 cupom encontrado"
                          : `${filters.total} cupons encontrados`
                      }
                      size="small"
                    />
                  </Box>

                  <Typography variant="subtitle1" color="text.secondary">
                    Gerencie os cupons promocionais
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenCreateDialog}
                >
                  Criar cupom
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  gap: {
                    xs: "1rem",
                  },
                  alignItems: {
                    sm: "center",
                  },
                  justifyContent: {
                    xs: "end",
                  },
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                    gap: "0.75rem",
                    alignItems: {
                      sm: "center",
                    },
                    justifyContent: {
                      xs: "start",
                      md: "end",
                    },
                    flexWrap: "wrap",
                  }}
                >
                  <SearchInput
                    label="Pesquisar cupons"
                    onSearch={handleSearch}
                    onChangeFilter={(filter) =>
                      setFilters((prev) => ({
                        ...prev,
                        internalName: filter,
                      }))
                    }
                    value={filters.internalName}
                    isLoading={isLoading}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="coupons-filter-label">Status</InputLabel>
                    <Select
                      labelId="coupons-filter-label"
                      label="Status"
                      value={filters.active}
                      onChange={handleActiveFilterChange}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="active">Ativos</MenuItem>
                      <MenuItem value="inactive">Inativos</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {isLoading && coupons.length === 0 ? (
              <Box
                alignItems="center"
                display="flex"
                flexDirection="column"
                marginTop={4}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {errorMessage}
                  </Alert>
                )}

                {isLoading && coupons.length > 0 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mb={2}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}

                {coupons.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      p: { xs: 4, md: 6 },
                      textAlign: "center",
                      backgroundColor: "#ffffff",
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      Nenhum cupom encontrado.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ajuste os filtros ou crie um novo cupom.
                    </Typography>
                  </Paper>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      p: { xs: 1, md: 2 },
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Stack spacing={{ xs: 1.5, md: 2 }}>
                      {coupons.map((coupon) => {
                      const isEditingCurrent =
                        formMode === "edit" &&
                        editingCouponId === coupon.id &&
                        isFormDialogOpen;
                      const isProcessingEdit =
                        isEditingCurrent && (isFormLoading || isFormSubmitting);
                      const isDeletingCurrent =
                        deletingCoupon && deleteTarget?.id === coupon.id;
                      const editButtonLabel = isProcessingEdit
                        ? isFormLoading
                          ? "Carregando..."
                          : "Salvando..."
                        : "Editar";
                      const deleteButtonLabel = isDeletingCurrent
                        ? "Excluindo..."
                        : "Excluir";

                      return (
                        <CouponCard
                          key={coupon.id}
                          internalName={coupon.internalName}
                          active={coupon.active}
                          discountTypeLabel={getDiscountTypeLabel(
                            coupon.discountType
                          )}
                          createdAtLabel={formatDate(coupon.createdAt)}
                          updatedAtLabel={formatDate(coupon.updatedAt)}
                          discountValueLabel={getCouponDiscountValueLabel(
                            coupon
                          )}
                          minOrderAmountLabel={getCouponMinOrderLabel(
                            coupon.minOrderAmountCents
                          )}
                          validityLabel={getCouponValidityLabel(coupon)}
                          expanded={Boolean(expandedCoupons[coupon.id])}
                          onToggle={(isExpanded) =>
                            handleToggleCoupon(coupon.id, isExpanded)
                          }
                          onEdit={() => handleOpenEditDialog(coupon)}
                          onDelete={() => handleOpenDeleteDialog(coupon)}
                          editDisabled={isProcessingEdit || isDeletingCurrent}
                          deleteDisabled={isDeletingCurrent}
                          editLabel={editButtonLabel}
                          deleteLabel={deleteButtonLabel}
                          editLoading={isProcessingEdit}
                          deleteLoading={isDeletingCurrent}
                        >
                          <Box display="flex" flexDirection="column" gap={2}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <Typography variant="subtitle1" fontWeight={600}>
                                Códigos do cupom
                              </Typography>
                              <Button
                                startIcon={<AddIcon />}
                                variant="outlined"
                                onClick={() =>
                                  handleOpenCreateCodeDialog(coupon)
                                }
                                disabled={isCodeSubmitting}
                              >
                                Criar código
                              </Button>
                            </Box>
                            <Divider />
                            {(() => {
                              const codesState = couponCodesState[coupon.id];
                              const isLoadingCodes =
                                codesState?.isLoading ?? false;
                              const hasCodes =
                                (codesState?.data.length ?? 0) > 0;
                              const hasMore =
                                !!codesState &&
                                codesState.data.length < codesState.total;

                              if (codesState?.error) {
                                return (
                                  <Alert severity="error">
                                    {codesState.error}
                                  </Alert>
                                );
                              }

                              if (!codesState) {
                                return (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Abra o card para carregar os códigos deste
                                    cupom.
                                  </Typography>
                                );
                              }

                              return (
                                <Box display="flex" flexDirection="column" gap={2}>
                                  {isLoadingCodes && !hasCodes ? (
                                    <Box
                                      display="flex"
                                      justifyContent="center"
                                      py={3}
                                    >
                                      <CircularProgress size={24} />
                                    </Box>
                                  ) : hasCodes ? (
                                    <>
                                      <Grid
                                        container
                                        spacing={{ xs: 1.5, md: 2 }}
                                      >
                                        {codesState.data.map((code) => {
                                          const isEditingThisCode =
                                            codeFormMode === "edit" &&
                                            editingCouponCodeId === code.id &&
                                            isCodeDialogOpen;
                                          const isDeleteDialogOpenForCode =
                                            codeDeleteTarget?.codeId === code.id;
                                          const isDeletingThisCode =
                                            isDeleteDialogOpenForCode && isDeletingCode;
                                          const editLabel =
                                            isEditingThisCode && isCodeSubmitting
                                              ? "Salvando..."
                                              : "Editar";
                                          const deleteLabel = isDeletingThisCode
                                            ? "Excluindo..."
                                            : "Excluir";

                                          const editDisabled =
                                            (isEditingThisCode && isCodeSubmitting) ||
                                            isDeleteDialogOpenForCode;
                                          const deleteDisabled =
                                            isDeleteDialogOpenForCode;

                                          return (
                                            <Grid
                                              item
                                              xs={12}
                                              md={6}
                                              key={code.id}
                                            >
                                              <CouponCodeCard
                                                code={code.code}
                                                active={code.active}
                                                assignedUserUids={code.assignedUserUid}
                                                assignedUsers={code.assignedUsers}
                                                assignedInstrumentIds={code.assignedInstrumentId}
                                                assignedInstruments={code.assignedInstruments}
                                                usageLimit={code.usageLimit}
                                                usageType={code.usageType}
                                                redemptionCount={code.redemptionCount}
                                                validityLabel={getCouponCodeValidityLabel(
                                                  code
                                                )}
                                                createdAtLabel={formatDate(
                                                  code.createdAt
                                                )}
                                                updatedAtLabel={formatDate(
                                                  code.updatedAt
                                                )}
                                                onEdit={() =>
                                                  handleOpenEditCodeDialog(coupon, code)
                                                }
                                                onDelete={() =>
                                                  handleOpenDeleteCodeDialog(
                                                    coupon,
                                                    code
                                                  )
                                                }
                                                editLabel={editLabel}
                                                deleteLabel={deleteLabel}
                                                editDisabled={editDisabled}
                                                deleteDisabled={deleteDisabled}
                                              />
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                      {isLoadingCodes && (
                                        <Box
                                          display="flex"
                                          justifyContent="center"
                                          py={1}
                                        >
                                          <CircularProgress size={20} />
                                        </Box>
                                      )}
                                      {hasMore && !isLoadingCodes && (
                                        <Box
                                          display="flex"
                                          justifyContent="center"
                                        >
                                          <Button
                                            variant="text"
                                            onClick={() =>
                                              handleLoadMoreCodes(coupon.id)
                                            }
                                          >
                                            Carregar mais
                                          </Button>
                                        </Box>
                                      )}
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Nenhum código encontrado para este cupom.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()}
                          </Box>
                        </CouponCard>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

                <Pagination
                  currentPage={filters.page}
                  totalPages={totalPages}
                  onChangePage={handlePageChange}
                />
              </>
            )}

          </Container>
        </Box>

        <CreateCouponDialog
          open={isFormDialogOpen}
          onClose={handleCloseFormDialog}
          onSubmit={handleSubmitForm}
          creating={isFormSubmitting}
          error={formError}
          formValues={formValues}
          onChange={handleFormChange}
          onDiscountTypeChange={handleDiscountTypeChange}
          title={formMode === "create" ? "Novo cupom" : "Editar cupom"}
          submitLabel={
            formMode === "create" ? "Criar cupom" : "Salvar alterações"
          }
          submittingLabel={
            formMode === "create" ? "Criando..." : "Salvando..."
          }
          disabled={isFormLoading}
          isLoading={isFormLoading}
        />

        <CreateCouponCodeDialog
          open={isCodeDialogOpen}
          onClose={handleCloseCodeDialog}
          onSubmit={handleSubmitCodeDialog}
          submitting={isCodeSubmitting}
          error={codeFormError}
          couponName={selectedCouponForCode?.name}
          formValues={codeFormValues}
          onChange={handleCodeFormChange}
          title={
            codeFormMode === "create"
              ? "Novo código de cupom"
              : "Editar código de cupom"
          }
          submitLabel={
            codeFormMode === "create" ? "Criar código" : "Salvar alterações"
          }
          submittingLabel={
            codeFormMode === "create" ? "Criando..." : "Salvando..."
          }
          defaultSelectedUsers={codeFormInitialUsers}
          couponStartsAt={selectedCouponForCode?.startsAt ?? null}
          couponEndsAt={selectedCouponForCode?.endsAt ?? null}
        />

        <Dialog
          open={Boolean(codeDeleteTarget)}
          onClose={isDeletingCode ? undefined : handleCloseDeleteCodeDialog}
        >
          <DialogTitle>Excluir código</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              {deleteCodeError && <Alert severity="error">{deleteCodeError}</Alert>}
              <Typography variant="body2">
                Tem certeza que deseja excluir o código{" "}
                <Box component="span" fontWeight={600}>
                  {codeDeleteTarget?.codeValue ?? ""}
                </Box>{" "}
                do cupom{" "}
                <Box component="span" fontWeight={600}>
                  {codeDeleteTarget?.couponName ?? ""}
                </Box>
                ? Essa ação não pode ser desfeita.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteCodeDialog}
              color="inherit"
              disabled={isDeletingCode}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDeleteCode}
              color="error"
              variant="contained"
              disabled={isDeletingCode}
            >
              {isDeletingCode ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteTarget)}
          onClose={deletingCoupon ? undefined : handleCloseDeleteDialog}
        >
          <DialogTitle>Excluir cupom</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              {deleteError && <Alert severity="error">{deleteError}</Alert>}
              <Typography variant="body2">
                Tem certeza que deseja excluir o cupom{" "}
                <Box component="span" fontWeight={600}>
                  {deleteTarget?.name ?? ""}
                </Box>
                ? Essa ação não pode ser desfeita.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              color="inherit"
              disabled={deletingCoupon}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deletingCoupon}
            >
              {deletingCoupon ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogActions>
        </Dialog>
      </Layout>
    </div>
  );
};

export default withNavigationHooks(Coupons);
